import { IAuditState, STATUS } from '../../global/types/audit'
import { AUDIT_PROGRESS, AI_DECISION } from '../../global/constants/audit'
import type { AuditConfig } from '../../global/context/audit-config-context/AuditConfigContext'

function getNextSection(
  currentSection: string,
  auditState: IAuditState | null,
  auditStatus: STATUS | null,
  configs: Record<STATUS, AuditConfig>,
  skipAIPassed: boolean = true,
  includeCurrentSection: boolean = false,
): string {
  let nextSection: string = currentSection
  if (auditStatus && configs[auditStatus]) {
    // If includeCurrentSection is false (default), advance to next section before checking
    // This is the expected behavior when clicking "Next" button
    if (!includeCurrentSection) {
      nextSection = configs[auditStatus].SECTIONS_ORDER[currentSection] || currentSection
    }

    // Skip sections where AI has already passed (unless manual review enabled)
    // Keep looping until we find a section that needs human review or reach completion
    if (skipAIPassed) {
      while (
        nextSection !== AUDIT_PROGRESS.completed &&
        nextSection !== AUDIT_PROGRESS.needsCoaching &&
        auditState?.[nextSection]?.aiReview?.decision === AI_DECISION.pass
      ) {
        const tempNext = configs[auditStatus].SECTIONS_ORDER[nextSection]
        if (!tempNext || tempNext === nextSection) {
          // Reached end of sections
          break
        }
        nextSection = tempNext
      }
    }
  }
  if (nextSection === AUDIT_PROGRESS.completed && auditState) {
    const auditNeedsCoaching = Object.values(auditState).some(
      (section) => section.coaching?.needsCoaching,
    )
    if (auditNeedsCoaching) {
      nextSection = AUDIT_PROGRESS.needsCoaching
    }
  }
  return nextSection
}

function getPreviousSectionInOrder(
  currentSection: string,
  initialSection: string,
  sectionsOrder: { [key: string]: string },
) {
  const previousSection =
    Object.entries(sectionsOrder).find(
      ([_, value]) => value === currentSection,
    )?.[0] || initialSection
  return previousSection
}

function getPreviousSection(
  currentSection: string,
  auditState: IAuditState | null,
  auditStatus: STATUS | null,
  configs: Record<STATUS, AuditConfig>,
  skipAIPassed: boolean = true,
) {
  if (!auditStatus || !configs[auditStatus]) {
    return currentSection
  }

  const config = configs[auditStatus]
  const sections = config.SECTIONS
  const sectionsOrder = config.SECTIONS_ORDER

  // Get the first section key as initialSection
  const initialSection = Object.values(sections)[0] || ''

  let previousSection = getPreviousSectionInOrder(
    currentSection,
    initialSection,
    sectionsOrder,
  )

  // Skip sections where AI has already passed (unless manual review enabled)
  if (skipAIPassed) {
    while (
      previousSection !== initialSection &&
      auditState?.[previousSection]?.aiReview?.decision === AI_DECISION.pass
    ) {
      const tempPrevious = getPreviousSectionInOrder(
        previousSection,
        initialSection,
        sectionsOrder,
      )
      if (tempPrevious === previousSection) {
        // Can't go back further
        break
      }
      previousSection = tempPrevious
    }
  }

  return previousSection
}

export { getNextSection, getPreviousSection }
